/**
 * `/approve/:token` — the page a client opens from the approval link (FR-E15, B-09).
 *
 * **Outside the shell and outside the session guard**, like `/callback`: the client is not a
 * user of this app and never signs in (ADR-0002; CLAUDE.md §7, no login screen). It shows the
 * client's name and the month, the workbook to download, and two actions — Approve, or Raise
 * a point. The design system's own components and tokens, nothing app-owned (ADR-0023).
 *
 * Opening the page spends nothing: only Approve uses the link up, so a mail scanner that
 * opens it first leaves it working.
 */

import { Alert, Button, Card, Input, Textarea } from '@fiveox/ui'
import { CheckCircle2, Download, FileSpreadsheet } from 'lucide-react'
import { useEffect, useState } from 'react'
import type { ReactNode } from 'react'
import { useParams } from 'react-router'

import {
  type ClientApprovalView,
  LinkRefused,
  canApprove,
  canRaise,
  clientApproval,
  heading,
} from '../lib/clientApproval'
import { saveFile } from '../lib/files'

function Frame({ children }: { children: ReactNode }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4 py-8">
      <Card className="w-full max-w-lg p-6 sm:p-8">{children}</Card>
    </div>
  )
}

export function ClientApproval() {
  const { token = '' } = useParams()
  const [view, setView] = useState<ClientApprovalView | null>(null)
  const [refused, setRefused] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [name, setName] = useState('')
  const [note, setNote] = useState('')
  const [raising, setRaising] = useState(false)

  useEffect(() => {
    let cancelled = false
    clientApproval
      .view(token)
      .then((v) => !cancelled && setView(v))
      .catch((e: unknown) => {
        if (!cancelled) setRefused(e instanceof Error ? e.message : String(e))
      })
    return () => {
      cancelled = true
    }
  }, [token])

  const attempt = async (fn: () => Promise<void>) => {
    setBusy(true)
    setError(null)
    try {
      await fn()
    } catch (e: unknown) {
      if (e instanceof LinkRefused && e.final) setRefused(e.message)
      else setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  if (refused) {
    return (
      <Frame>
        <h1 className="mb-2 text-lg font-semibold text-foreground">This link cannot be used</h1>
        <p className="text-sm leading-relaxed text-muted-foreground">{refused}</p>
      </Frame>
    )
  }
  if (done) {
    return (
      <Frame>
        <CheckCircle2 className="mb-4 text-muted-foreground" size={28} />
        <h1 className="mb-2 text-lg font-semibold text-foreground">Approved</h1>
        <p className="text-sm leading-relaxed text-muted-foreground">{done}</p>
      </Frame>
    )
  }
  if (!view) {
    return (
      <Frame>
        <p className="text-sm text-muted-foreground">Opening the approval…</p>
      </Frame>
    )
  }

  return (
    <Frame>
      <p className="mb-1 text-sm text-muted-foreground">{view.client_name}</p>
      <h1 className="mb-4 text-lg font-semibold text-foreground">{heading(view)}</h1>
      <p className="mb-4 text-sm leading-relaxed text-muted-foreground">
        Your accountant has prepared this return and asks you to check the workbook below. Approve
        it if it is right, or raise a point if something needs a change. This link works until{' '}
        {view.expires_on}.
      </p>

      {error && <Alert tone="error" className="mb-4">{error}</Alert>}
      {notice && <Alert tone="success" className="mb-4">{notice}</Alert>}

      <Button
        type="button"
        variant="outline"
        className="mb-6 w-full justify-start"
        disabled={busy}
        onClick={() =>
          void attempt(async () => saveFile(await clientApproval.workbook(token), view.workbook_filename))
        }
      >
        <FileSpreadsheet size={16} />
        <span className="truncate">{view.workbook_filename}</span>
        <Download size={16} className="ml-auto" />
      </Button>

      <Input
        label="Your name"
        value={name}
        onChange={(e) => setName(e.target.value.slice(0, 150))}
        placeholder="Recorded as who approved"
        autoComplete="name"
      />

      <div className="mt-4 flex flex-wrap gap-2">
        <Button
          type="button"
          disabled={busy || !canApprove(name)}
          onClick={() =>
            void attempt(async () => {
              const result = await clientApproval.approve(token, name)
              setDone(result.message)
            })
          }
        >
          Approve
        </Button>
        <Button
          type="button"
          variant="outline"
          disabled={busy || view.points_left === 0}
          onClick={() => setRaising((r) => !r)}
        >
          Raise a point
        </Button>
      </div>

      {raising && (
        <div className="mt-4">
          <Textarea
            label="The point you want to raise"
            rows={4}
            value={note}
            onChange={(e) => setNote(e.target.value.slice(0, 2000))}
            placeholder="What needs a change, or a question for your accountant"
          />
          <Button
            type="button"
            className="mt-2"
            disabled={busy || !canRaise(note, view.points_left)}
            onClick={() =>
              void attempt(async () => {
                const result = await clientApproval.raisePoint(token, note, name)
                setNotice(result.message)
                setNote('')
                setRaising(false)
                setView({ ...view, points_left: result.points_left })
              })
            }
          >
            Send the point
          </Button>
        </div>
      )}
    </Frame>
  )
}
